// frontend/src/pages/DSALearning.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  vscDarkPlus,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { dsaAPI, userAPI } from "../utils/api";
import { logoutIfExpired } from "../utils/auth";
import {
  dsaRoadmap,
  availableLanguages,
  platformStyles,
  difficultyLevels,
} from "../data/dsaRoadmap";
import ModelSelector from "../components/ModelSelector";
import Toast from "../components/Toast";
import LoadingSpinner from "../components/LoadingSpinner";
import ApiErrorHandler from "../components/ApiErrorHandler";

const DSALearning = ({ user }) => {
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("accessai-dsa-language") || "javascript"
  );
  const [currentTopic, setCurrentTopic] = useState(null);
  const [lessonContent, setLessonContent] = useState("");
  const [generatedProblem, setGeneratedProblem] = useState("");
  const [codeToReview, setCodeToReview] = useState("");
  const [codeReview, setCodeReview] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingProblem, setLoadingProblem] = useState(false);
  const [loadingReview, setLoadingReview] = useState(false);
  const [problemStyle, setProblemStyle] = useState("leetcode");
  const [problemDifficulty, setProblemDifficulty] = useState("medium");
  const [includeEditorial, setIncludeEditorial] = useState(false);
  const [userProgress, setUserProgress] = useState(() => {
    const saved = localStorage.getItem("accessai-dsa-progress");
    return saved
      ? JSON.parse(saved)
      : { completedTopics: {}, language: "javascript" };
  });
  const [expandedLevels, setExpandedLevels] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(
    user?.preferredModel || "xiaomi/mimo-v2-flash:free"
  );
  const [modelStats, setModelStats] = useState({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showGenerateButton, setShowGenerateButton] = useState(true);

  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const messagesEndRef = useRef(null);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();

  const showToast = (message, type = "info") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 4000);
  };

  const closeToast = () => {
    setToast({ show: false, message: "", type: "" });
  };

  // Theme persistence
  useEffect(() => {
    const savedTheme = localStorage.getItem("accessai-theme") || "light";
    document.documentElement.setAttribute("data-theme", savedTheme);
  }, []);

  useEffect(() => {
    if (user && logoutIfExpired(user)) {
      setApiError({
        status: 401,
        message: "Your session has expired. Please login again."
      });
      navigate("/login");
    }
  }, [user, navigate]);

  useEffect(() => {
    localStorage.setItem("accessai-dsa-language", selectedLanguage);
    setUserProgress((prev) => ({ ...prev, language: selectedLanguage }));
  }, [selectedLanguage]);

  useEffect(() => {
    localStorage.setItem("accessai-dsa-progress", JSON.stringify(userProgress));
  }, [userProgress]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lessonContent, generatedProblem, codeReview]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sidebarOpen]);

  const isTopicCompleted = (topicId) => {
    return userProgress.completedTopics[topicId] || false;
  };

  const markTopicComplete = (topicId) => {
    setUserProgress((prev) => ({
      ...prev,
      completedTopics: { ...prev.completedTopics, [topicId]: true },
    }));

    if (user) {
      userAPI
        .updatePoints(10, `completed_${topicId}`)
        .then(() => showToast("Topic marked as complete! +10 XP", "success"))
        .catch(console.error);
    } else {
      showToast("Topic marked as complete!", "info");
    }
  };

  const loadDSALesson = async (topic) => {
    if (!topic) return;

    if (!user) {
      showToast("Please login to access DSA lessons", "warning");
      navigate("/login");
      return;
    }

    if (logoutIfExpired(user)) {
      setApiError({
        status: 401,
        message: "Session expired. Please login again."
      });
      return;
    }

    setCurrentTopic(topic);
    setLessonContent("");
    setGeneratedProblem("");
    setCodeReview("");
    setShowGenerateButton(true);
    
    // Don't load content automatically, just show the topic
    setLessonContent("");
    
    showToast(
      `Topic selected: ${topic.title}. Click "Generate Lesson" to load content.`,
      "info"
    );
  };

  const generateLessonContent = async () => {
    if (!currentTopic) {
      showToast("Please select a topic first", "warning");
      return;
    }

    setShowGenerateButton(false);
    setLoading(true);
    setLessonContent("");

    setLessonContent(
      `## 🎯 Generating content for your lesson\n\nPlease wait while we prepare the lesson material for: **${currentTopic.title}** in **${selectedLanguage}**`
    );

    try {
      const response = await dsaAPI.getLesson({
        topic: currentTopic.title,
        language: selectedLanguage,
        style: "explanation",
        model: selectedModel,
      });

      if (response.data.success) {
        setLessonContent(response.data.content);
        showToast(
          `Lesson loaded! +${response.data.pointsAwarded} XP`,
          "success"
        );
      }
    } catch (error) {
      console.error("Error loading DSA lesson:", error);

      if (error.response?.status === 401 || error.response?.status === 403) {
        logoutIfExpired(user);
        setApiError({
          status: error.response?.status,
          message: "Session expired. Please login again.",
          data: error.response?.data
        });
        navigate("/login");
      } else if (error.response?.status === 429) {
        setApiError({
          status: error.response?.status,
          message: error.response?.data?.details || error.message,
          data: error.response?.data
        });
      } else {
        setApiError({
          status: error.response?.status,
          message: "Failed to load lesson content. Please try again.",
          data: error.response?.data
        });
        setLessonContent(
          `## ⚠️ Service Unavailable\n\nWe're having trouble connecting to our AI service.\n\n**Please try:**\n1. Check your internet connection\n2. Try a different AI model\n3. Wait a few minutes and try again\n\n*If this continues, contact support.*`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const generateDSAProblem = async () => {
    if (!currentTopic) {
      showToast("Please select a topic first", "warning");
      return;
    }

    if (!user) {
      showToast("Please login to generate problems", "warning");
      navigate("/login");
      return;
    }

    if (logoutIfExpired(user)) {
      setApiError({
        status: 401,
        message: "Session expired. Please login again."
      });
      return;
    }

    setLoadingProblem(true);
    setGeneratedProblem("");

    setGeneratedProblem(
      `## ⏳ Generating problem...\n\nCreating a unique **${problemDifficulty}** level problem about **${currentTopic.title}** in **${selectedLanguage}** style...`
    );

    try {
      const response = await dsaAPI.generateProblem({
        topic: currentTopic.title,
        difficulty: problemDifficulty,
        style: problemStyle,
        language: selectedLanguage,
        includeEditorial: includeEditorial,
        model: selectedModel,
      });

      if (response.data.success) {
        setGeneratedProblem(response.data.problem);
        showToast("Problem generated successfully!", "success");
      }
    } catch (error) {
      console.error("Error generating problem:", error);

      if (error.response?.status === 429) {
        setApiError({
          status: error.response?.status,
          message: error.response?.data?.details || error.message,
          data: error.response?.data
        });
      } else {
        setApiError({
          status: error.response?.status,
          message: "Failed to generate problem. Please try again.",
          data: error.response?.data
        });
        setGeneratedProblem(
          `## ⚠️ Service Unavailable\n\nWe're having trouble generating the problem.\n\n**Please try:**\n1. Check your internet connection\n2. Try a different AI model\n3. Wait a few minutes and try again\n\n*If this continues, contact support.*`
        );
      }
    } finally {
      setLoadingProblem(false);
    }
  };

  const reviewCode = async () => {
    if (!codeToReview.trim()) {
      showToast("Please enter code to review", "warning");
      return;
    }

    if (!user) {
      showToast("Please login to review code", "warning");
      navigate("/login");
      return;
    }

    if (logoutIfExpired(user)) {
      setApiError({
        status: 401,
        message: "Session expired. Please login again."
      });
      return;
    }

    setLoadingReview(true);
    setCodeReview("");

    setCodeReview(
      `## 🔍 Analyzing your code...\n\nReviewing your **${selectedLanguage}** code...\n\n**Code Preview:**\n\`\`\`${selectedLanguage}\n${
        codeToReview.length > 200
          ? codeToReview.substring(0, 200) + "..."
          : codeToReview
      }\n\`\`\``
    );

    try {
      const response = await dsaAPI.reviewCode({
        code: codeToReview,
        language: selectedLanguage,
        problemContext: currentTopic?.title || "General code review",
        model: selectedModel,
      });

      if (response.data.success) {
        setCodeReview(response.data.review);
        showToast("Code reviewed successfully!", "success");
      }
    } catch (error) {
      console.error("Error reviewing code:", error);

      if (error.response?.status === 429) {
        setApiError({
          status: error.response?.status,
          message: error.response?.data?.details || error.message,
          data: error.response?.data
        });
      } else {
        setApiError({
          status: error.response?.status,
          message: "Failed to review code. Please try again.",
          data: error.response?.data
        });
        setCodeReview(
          `## ⚠️ Service Unavailable\n\nWe're having trouble reviewing your code.\n\n**Please try:**\n1. Check your internet connection\n2. Try a different AI model\n3. Wait a few minutes and try again\n\n*If this continues, contact support.*`
        );
      }
    } finally {
      setLoadingReview(false);
    }
  };

  const toggleLevel = (levelId) => {
    setExpandedLevels((prev) =>
      prev.includes(levelId)
        ? prev.filter((id) => id !== levelId)
        : [...prev, levelId]
    );
  };

  const calculateProgress = () => {
    const allTopics = dsaRoadmap.levels.reduce((acc, level) => {
      level.topics.forEach((topic) => {
        topic.subtopics.forEach((subtopic) => acc.push(subtopic.id));
      });
      return acc;
    }, []);

    const completed = allTopics.filter((topicId) =>
      isTopicCompleted(topicId)
    ).length;
    return {
      completed,
      total: allTopics.length,
      percentage:
        allTopics.length > 0
          ? Math.round((completed / allTopics.length) * 100)
          : 0,
    };
  };

  const progress = calculateProgress();

  // Handle model switch from error handler
  const handleSwitchModel = async (modelName) => {
    if (!user) return;
    
    // Find model ID by display name
    const model = availableModels.find(m => 
      m.name === modelName || m.displayName === modelName
    );
    
    if (model) {
      setSelectedModel(model.id);
      setApiError(null);
      showToast(`Switched to ${modelName}`, "success");
    }
  };

  return (
    <div className="app-container">
      {/* Header - Responsive */}
      <header className="header">
        <div className="header-left">
          <button
            className="btn btn-secondary btn-sm lg:hidden p-2"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
          >
            {sidebarOpen ? "✕" : "☰"}
          </button>
          <h1 className="app-title ml-2 md:ml-4 truncate">AccessAI DSA Learning</h1>
        </div>

        <div className="header-right">
          <div className="hidden sm:flex flex-col items-end text-xs">
            <span className="font-semibold text-primary">
              Progress: {progress.percentage}%
            </span>
            <span className="text-text-secondary">
              Topics: {progress.completed}/{progress.total}
            </span>
          </div>

          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="input-field text-xs md:text-sm"
            style={{ minWidth: "100px", maxWidth: "140px" }}
          >
            {availableLanguages.map((lang) => (
              <option key={lang.id} value={lang.id}>
                {lang.icon} {lang.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => navigate("/")}
            className="btn btn-primary btn-sm text-xs md:text-sm whitespace-nowrap"
          >
            ← Back to Chat
          </button>
        </div>
      </header>

      <div className="app-layout">
        {/* DSA Sidebar - Responsive */}
        <aside
          ref={sidebarRef}
          className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}
        >
          <div className="sidebar-header">
            <h2 className="text-lg md:text-xl">DSA Roadmap</h2>
            <button
              className="btn btn-secondary btn-sm lg:hidden p-1.5"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              ✕
            </button>
          </div>

          {/* Progress Tracker */}
          <div className="card mb-3 md:mb-4">
            <div className="progress-container">
              <div className="progress-label text-xs md:text-sm">
                <span>Overall Progress</span>
                <span>{progress.percentage}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
            </div>
          </div>

          {/* Model Selector */}
          {user && (
            <div className="card mb-3 md:mb-4">
              <ModelSelector
                user={user}
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
                onModelStatsUpdate={setModelStats}
              />
            </div>
          )}

          {/* DSA Roadmap Levels - Responsive */}
          <div className="space-y-2">
            {dsaRoadmap.levels.map((level) => (
              <div key={level.id} className="card p-2 md:p-3">
                <button
                  onClick={() => toggleLevel(level.id)}
                  className="w-full text-left flex justify-between items-center"
                >
                  <span className="font-medium text-sm md:text-base truncate">{level.title}</span>
                  <span className="text-lg">{expandedLevels.includes(level.id) ? "−" : "+"}</span>
                </button>

                {expandedLevels.includes(level.id) && (
                  <div className="mt-1.5 md:mt-2 space-y-1">
                    {level.topics.map((topic) => (
                      <div key={topic.id} className="ml-1 md:ml-2">
                        <h4 className="text-xs md:text-sm font-medium text-text-secondary mb-0.5 md:mb-1 truncate">
                          {topic.title}
                        </h4>
                        <div className="space-y-0.5 md:space-y-1">
                          {topic.subtopics.map((subtopic) => (
                            <button
                              key={subtopic.id}
                              onClick={() => loadDSALesson(subtopic)}
                              className={`w-full text-left p-1.5 md:p-2 rounded text-xs md:text-sm flex justify-between items-center truncate ${
                                currentTopic?.id === subtopic.id
                                  ? "bg-primary-light text-primary"
                                  : "hover:bg-surface-elevated"
                              } ${
                                isTopicCompleted(subtopic.id)
                                  ? "border-l-2 border-success"
                                  : ""
                              }`}
                            >
                              <span className="truncate">{subtopic.title}</span>
                              {isTopicCompleted(subtopic.id) && (
                                <span className="text-success text-sm">✓</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content - Responsive */}
        <main className="main-content">
          {/* Problem Generator Controls - Responsive */}
          <div className="card mb-4 md:mb-6">
            <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Problem Generator</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-3 md:mb-4">
              <div>
                <label className="block text-xs md:text-sm font-medium text-text-secondary mb-1 md:mb-2">
                  Problem Style
                </label>
                <select
                  value={problemStyle}
                  onChange={(e) => setProblemStyle(e.target.value)}
                  className="input-field text-xs md:text-sm"
                >
                  {platformStyles.map((style) => (
                    <option key={style.id} value={style.id}>
                      {style.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-medium text-text-secondary mb-1 md:mb-2">
                  Difficulty
                </label>
                <select
                  value={problemDifficulty}
                  onChange={(e) => setProblemDifficulty(e.target.value)}
                  className="input-field text-xs md:text-sm"
                >
                  {difficultyLevels.map((diff) => (
                    <option key={diff.id} value={diff.id}>
                      {diff.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-1.5 md:gap-2">
                  <input
                    type="checkbox"
                    checked={includeEditorial}
                    onChange={(e) => setIncludeEditorial(e.target.checked)}
                    className="rounded w-4 h-4 md:w-5 md:h-5"
                  />
                  <span className="text-xs md:text-sm">Include Editorial</span>
                </label>
              </div>

              <div className="flex items-end">
                <button
                  onClick={generateDSAProblem}
                  disabled={loadingProblem || !currentTopic}
                  className="btn btn-primary w-full text-xs md:text-sm py-2"
                >
                  {loadingProblem ? (
                    <LoadingSpinner size="sm" text="Generating..." />
                  ) : (
                    "🎯 Generate Problem"
                  )}
                </button>
              </div>
            </div>

            {/* Current Model Info */}
            {selectedModel && modelStats[selectedModel] && (
              <div className="text-xs md:text-sm text-text-muted">
                Using:{" "}
                <span className="font-medium">
                  {modelStats[selectedModel]?.displayName}
                </span>
                <span className="ml-1 md:ml-2">
                  (Used: {modelStats[selectedModel]?.usedToday || 0}/
                  {modelStats[selectedModel]?.dailyLimit || 0})
                </span>
              </div>
            )}
          </div>

          {/* Current Topic Info - Responsive */}
          {currentTopic && (
            <div className="card mb-4 md:mb-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg md:text-xl lg:text-2xl font-bold mb-1 md:mb-2 truncate">
                    {currentTopic.title}
                  </h2>
                  <div className="flex flex-wrap gap-1 md:gap-2">
                    <span className="badge badge-primary text-xs">
                      {currentTopic.id.split(".")[0] === "1"
                        ? "Beginner"
                        : currentTopic.id.split(".")[0] === "2"
                        ? "Beginner-Intermediate"
                        : currentTopic.id.split(".")[0] === "3"
                        ? "Intermediate"
                        : currentTopic.id.split(".")[0] === "4"
                        ? "Data Structures"
                        : currentTopic.id.split(".")[0] === "5"
                        ? "Advanced"
                        : "Competitive"}
                    </span>
                    <span className="badge badge-secondary text-xs">
                      {selectedLanguage.toUpperCase()}
                    </span>
                    {selectedModel && (
                      <span className="badge badge-primary text-xs truncate max-w-[100px]">
                        {modelStats[selectedModel]?.displayName?.split(" ")[1] || selectedModel}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 mt-2 md:mt-0">
                  {!isTopicCompleted(currentTopic.id) ? (
                    <button
                      onClick={() => markTopicComplete(currentTopic.id)}
                      className="btn btn-success text-xs md:text-sm py-1.5 md:py-2"
                    >
                      ✅ Mark Complete
                    </button>
                  ) : (
                    <button
                      className="btn btn-secondary text-xs md:text-sm py-1.5 md:py-2"
                      disabled
                      style={{ opacity: 0.7 }}
                    >
                      ✓ Completed
                    </button>
                  )}
                  
                  {showGenerateButton && (
                    <button
                      onClick={generateLessonContent}
                      disabled={loading}
                      className="btn btn-primary text-xs md:text-sm py-1.5 md:py-2"
                    >
                      {loading ? "Generating..." : "📚 Generate Lesson"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Lesson Content - Responsive */}
          {lessonContent && (
            <div className="card mb-4 md:mb-6">
              <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">📖 Lesson Content</h3>
              <div className="prose max-w-none text-sm md:text-base">
                <ReactMarkdown
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || "");
                      return !inline && match ? (
                        <div className="code-block my-2 md:my-4">
                          <div className="code-header">
                            <span className="code-language text-xs">{match[1]}</span>
                            <button
                              onClick={() =>
                                navigator.clipboard.writeText(String(children))
                              }
                              className="btn btn-secondary btn-sm text-xs"
                            >
                              Copy
                            </button>
                          </div>
                          <div className="code-content">
                            <SyntaxHighlighter
                              style={vscDarkPlus}
                              language={match[1]}
                              PreTag="div"
                              customStyle={{ fontSize: '0.75rem', padding: '0.5rem' }}
                              {...props}
                            >
                              {String(children).replace(/\n$/, "")}
                            </SyntaxHighlighter>
                          </div>
                        </div>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {lessonContent}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Generated Problem - Responsive */}
          {generatedProblem && (
            <div className="card mb-4 md:mb-6">
              <div className="flex justify-between items-center mb-3 md:mb-4">
                <h3 className="text-lg md:text-xl font-bold">🎯 Generated Problem</h3>
                <span className={`badge badge-${problemStyle} text-xs md:text-sm`}>
                  {platformStyles.find((s) => s.id === problemStyle)?.name}
                </span>
              </div>
              <div className="prose max-w-none text-sm md:text-base">
                <ReactMarkdown>{generatedProblem}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Code Review Section - Responsive */}
          <div className="card">
            <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">🔍 Code Review</h3>

            <div className="mb-3 md:mb-4">
              <textarea
                value={codeToReview}
                onChange={(e) => setCodeToReview(e.target.value)}
                placeholder={`Paste your ${selectedLanguage} code here for review...`}
                className="input-field text-xs md:text-sm"
                rows={6}
                style={{ fontFamily: "monospace" }}
              />
              <button
                onClick={reviewCode}
                disabled={loadingReview || !codeToReview.trim()}
                className="btn btn-primary mt-1.5 md:mt-2 text-xs md:text-sm py-1.5 md:py-2"
              >
                {loadingReview ? (
                  <LoadingSpinner size="sm" text="Reviewing..." />
                ) : (
                  "Review Code"
                )}
              </button>
            </div>

            {codeReview && (
              <div className="prose max-w-none text-sm md:text-base">
                <ReactMarkdown>{codeReview}</ReactMarkdown>
              </div>
            )}
          </div>

          <div ref={messagesEndRef} />
        </main>
      </div>

      {/* Toast Notifications */}
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={closeToast} />
      )}

      {/* Global API Error Handler */}
      <ApiErrorHandler
        error={apiError}
        onClose={() => setApiError(null)}
        onSwitchModel={handleSwitchModel}
      />
    </div>
  );
};

export default DSALearning;